import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApi, postApi } from '../../services/apiServices';
import { toast } from 'react-toastify';
import Modal from 'react-modal';

function Customers() {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState('All');
    const [showFilter, setShowFilter] = useState(false);

    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 50,
        total: 0,
        last_page: 1
    });

    const filterPopupRef = useRef(null);

    useEffect(() => {
        if (!showFilter) return;
        function handleClickOutside(event) {
            if (filterPopupRef.current && !filterPopupRef.current.contains(event.target)) {
                setShowFilter(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showFilter]);

    const [filters, setFilters] = useState({
        last_login_from: '',
        last_login_to: '',
        spending_min: '',
        spending_max: '',
        bundles_min: '',
        bundles_max: ''
    });

    const validateNumberInput = (value) => {
      
        if (value === '' || /^\d+$/.test(value)) {
            return true;
        }
        return false;
    };

    const handleNumberInputChange = (field, value) => {
        if (validateNumberInput(value)) {
            setFilters(f => ({ ...f, [field]: value }));
        }
    };

    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailForm, setEmailForm] = useState({
        email: '',
        subject: '',
        description: ''
    });
    const [emailLoading, setEmailLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const customStyles = {
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            border: 'none',
            borderRadius: '10px',
            padding: '0',
            maxWidth: '600px',
            width: '90%',
            backgroundColor: 'transparent'
        },
        overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000
        }
    };

    const fetchCustomers = async (searchKeyword = '', filterValues = null, page = 1) => {
        console.log('fetchCustomers called with:', { searchKeyword, filterValues, page });
        try {
            setLoading(true);
            setError(null);

            const currentFilters = filterValues || filters;

            let params = [];
            if (searchKeyword.trim()) params.push(`search=${encodeURIComponent(searchKeyword.trim())}`);
            if (currentFilters.last_login_from) params.push(`last_login_from=${currentFilters.last_login_from}`);
            if (currentFilters.last_login_to) params.push(`last_login_to=${currentFilters.last_login_to}`);
            if (currentFilters.spending_min) params.push(`spending_min=${currentFilters.spending_min}`);
            if (currentFilters.spending_max) params.push(`spending_max=${currentFilters.spending_max}`);
            if (currentFilters.bundles_min) params.push(`bundles_min=${currentFilters.bundles_min}`);
            if (currentFilters.bundles_max) params.push(`bundles_max=${currentFilters.bundles_max}`);
            
            params.push(`page=${page}`);

            let apiUrl = '/api/customer/detail';
            if (params.length > 0) {
                apiUrl += '?' + params.join('&');
            }

            console.log('API URL:', apiUrl);

            const response = await getApi(apiUrl);

            console.log('API Response:', response);

            if (response?.status) {
                setCustomers(response.data || []);
                
                setPagination({
                    current_page: response.current_page || 1,
                    per_page: response.per_page || 10,
                    total: response.total || 0,
                    last_page: response.last_page || 1
                });
                console.log('Pagination state updated:', {
                    current_page: response.current_page || 1,
                    per_page: response.per_page || 10,
                    total: response.total || 0,
                    last_page: response.last_page || 1
                });
            } else {
                setError(response?.message || 'Failed to fetch customers');
                toast.error(response?.message || 'Failed to fetch customers');
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            setError('Failed to fetch customers. Please try again.');
            toast.error('Failed to fetch customers. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        if (window.searchTimeout) {
            clearTimeout(window.searchTimeout);
        }

        window.searchTimeout = setTimeout(() => {
            fetchCustomers(value, filters, 1); 
        }, 500);
    };

    const handleFilterClick = (filter) => {
        setActiveFilter(filter);
        
    };

    const handleFilterButtonClick = () => {
        setShowFilter(!showFilter);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        } catch (error) {
            return dateString;
        }
    };

    const formatCurrency = (amount) => {
        if (!amount || amount === 0) return '0';
        return amount.toString();
    };

    useEffect(() => {
        console.log('Component mounted, fetching customers...');
        fetchCustomers('', null, 1);
    }, []);

    const handlePageChange = (page) => {
        const pageNum = parseInt(page, 10);
        console.log('handlePageChange called with page:', pageNum);
        console.log('Current pagination state:', pagination);
        const lastPage = parseInt(pagination.last_page, 10);
        if (pageNum >= 1 && pageNum <= lastPage) {
            console.log('Fetching customers for page:', pageNum);
            fetchCustomers(searchQuery, filters, pageNum);
        } else {
            console.log('Page change rejected - page out of range');
        }
    };

    const handlePreviousPage = () => {
        console.log('handlePreviousPage called');
        const currentPage = parseInt(pagination.current_page, 10);
        const lastPage = parseInt(pagination.last_page, 10);
        console.log('Current page:', currentPage, 'Last page:', lastPage);
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        console.log('handleNextPage called');
        const currentPage = parseInt(pagination.current_page, 10);
        const lastPage = parseInt(pagination.last_page, 10);
        console.log('Current page:', currentPage, 'Last page:', lastPage);
        if (currentPage < lastPage) {
            handlePageChange(currentPage + 1);
        } else {
            console.log('Next page rejected - already at last page');
        }
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        const currentPage = parseInt(pagination.current_page, 10);
        const lastPage = parseInt(pagination.last_page, 10);
        
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(lastPage, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        
        return pages;
    };

    const openEmailModal = (customer) => {
        setSelectedCustomer(customer);
        setEmailForm({
            email: customer.email || '',
            subject: 'Special Offer',
            description: `Hello ${customer.name || 'Customer'}, Enjoy 20% off on your next top-up.`
        });
        setShowEmailModal(true);
    };

    const closeEmailModal = () => {
        if (emailLoading) return;
        setShowEmailModal(false);
    };

    const handleEmailFormChange = (e) => {
        const { name, value } = e.target;
        setEmailForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSendEmail = async () => {
        try {
            setEmailLoading(true);
            if (!emailForm.email || !emailForm.subject || !emailForm.description) {
                toast.error('Please fill in all required fields');
                return;
            }
            const payload = {
                email: emailForm.email,
                subject: emailForm.subject,
                content: emailForm.description
            };
            const response = await postApi('/api/send-email', payload);
            if (response?.status) {
                toast.success(response.message || 'Email sent successfully');
                closeEmailModal();
            } else {
                toast.error(response?.message || 'Failed to send email');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            toast.error(error?.message || 'Failed to send email. Please try again.');
        } finally {
            setEmailLoading(false);
        }
    };

    return (
        <>
            <div className="right-section main-page-cs">
                <div className="customer-top">
                    <div className="customer-top-left">
                    
                    </div>
                    <div className="customer-top-right">
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search by name / email"
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                        <button className="filter-btn" onClick={handleFilterButtonClick}>
                            <img src={window.getPublicUrl('admin/images/Filters.svg')} alt="icon" /> Filters
                        </button>
                    </div>
                </div>

                {showFilter && (
                    <div className='filter-pop-up' ref={filterPopupRef}>
                       <div className='pop-up-header'>
                        <h3>Filters</h3>
                        <button type="button" onClick={() => {
                            setFilters({
                                last_login_from: '',
                                last_login_to: '',
                                spending_min: '',
                                spending_max: '',
                                bundles_min: '',
                                bundles_max: ''
                            });
                            fetchCustomers(searchQuery, {}, 1);
                        }}>Reset</button>
                        </div>
                        <div className='pop-up-body'>
                            <form onSubmit={e => { e.preventDefault(); fetchCustomers(searchQuery, filters, 1); setShowFilter(false); }}>
                                <div className='row'>
                                    <div className='col-md-12'>
                                      
                                    </div>
                                    <div className='col-md-6'>
                                         <div className='form-group'>
                                            <label>Last Login</label>
                                            <input type='date' className='form-control' value={filters.last_login_from} onChange={e => setFilters(f => ({ ...f, last_login_from: e.target.value }))} />
                                        </div>
                                    </div>
                                     <div className='col-md-6'>
                                         <div className='form-group'>
                                            <label className='blank-lable'></label>
                                            <input type='date' className='form-control' value={filters.last_login_to} onChange={e => setFilters(f => ({ ...f, last_login_to: e.target.value }))} />
                                        </div>
                                     </div>
                                      <div className='col-md-12 seprator'>   <hr></hr></div>
                                    <div className='col-md-6'>
                                         <div className='form-group'>
                                            <label>Spending Range</label>
                                            <input type='text' placeholder='Min' className='form-control' value={filters.spending_min} onChange={e => handleNumberInputChange('spending_min', e.target.value)} />
                                            <img src={window.getPublicUrl('admin/images/range.png')} alt="icon" />
                                        </div>
                                    </div>
                                     <div className='col-md-6'>
                                         <div className='form-group'>
                                           <label className='blank-lable'></label>
                                            <input type='text' placeholder='Max' className='form-control' value={filters.spending_max} onChange={e => handleNumberInputChange('spending_max', e.target.value)} />
                                            <img src={window.getPublicUrl('admin/images/range.png')} alt="icon" />
                                        </div>
                                     </div>
                                       <div className='col-md-12 seprator'>   <hr></hr></div>
                                    <div className='col-md-6'>
                                         <div className='form-group'>
                                            <label>Bundles Bought</label>
                                            <input type='text' placeholder='Min' className='form-control' value={filters.bundles_min} onChange={e => handleNumberInputChange('bundles_min', e.target.value)} />
                                             <img src={window.getPublicUrl('admin/images/bundle.png')} alt="icon" />
                                        </div>
                                    </div>
                                     <div className='col-md-6'>
                                         <div className='form-group'>
                                            <label className='blank-lable'></label>
                                            <input type='text' placeholder='Max' className='form-control' value={filters.bundles_max} onChange={e => handleNumberInputChange('bundles_max', e.target.value)} />
                                             <img src={window.getPublicUrl('admin/images/bundle.png')} alt="icon" />
                                        </div>
                                     </div>
                                         <div className='col-md-12 mt-3'>
                                           <button className='btn apply-filter w-100' type="submit">Apply Filters</button>
                                     </div>
                                </div>
                            </form>
                    </div>
                    </div>
                )}

                <div className={`customer-tablle-main ${customers.length === 1 ? 'single-record-customer' : ''}`}>
                    {loading ? (
                        <div className="loader-overlay">
                            <span className="loader"></span>
                        </div>
                    ) : (
                        <table className="table">
                            <thead>
                                <tr>
                                    <th scope="col">Sr No.</th>
                                    <th className="name" scope="col">Name</th>
                                    <th scope="col" className="email">Email</th>
                                    <th scope="col" className="bought">Bundles Bought</th>
                                    <th scope="col" className="total-spent">Total Spent</th>
                                    <th scope="col" className="last-login">Last Login</th>
                                    <th scope="col" className="action">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customers.length > 0 ? (
                                    customers.map((customer, index) => {
                                        return (
                                            <tr key={customer.user_id || index}>
                                                <td>{index + 1}</td>
                                                <td className="name">
                                                    <span className="profile-img">
                                                        <img src={customer.profile_picture || window.getPublicUrl('admin/images/profile.svg')} alt="profile" />
                                                    </span>
                                                    {customer.name || 'N/A'}
                                                </td>
                                                <td className="email">{customer.email || 'N/A'}</td>
                                                <td className="bought">{customer.bundles_bought || 0}</td>
                                                <td className="total-spent">{formatCurrency(customer.total_spent)}</td>
                                                <td className="last-login">{formatDate(customer.last_login_at)}</td>
                                                <td className="action">
                                                    <div className="dropdown">
                                                        <button
                                                            className="btn action-btn dropdown-toggle"
                                                            type="button"
                                                            id={`dropdownMenuButton-${index}`}
                                                            data-toggle="dropdown"
                                                            aria-haspopup="true"
                                                            aria-expanded="false"
                                                        >
                                                            <img src={window.getPublicUrl('admin/images/action.svg')} alt="action" />
                                                        </button>
                                                        <div className="dropdown-menu" aria-labelledby={`dropdownMenuButton-${index}`}>
                                                            <a className="dropdown-item" href="#" onClick={(e) => {
                                                                e.preventDefault();
                                                                navigate(`/admin/viewCustomers/${customer.user_id}`);
                                                            }}>
                                                                <img src={window.getPublicUrl('admin/images/view.svg')} alt="view" /> View profile
                                                            </a>
                                                            <a className="dropdown-item" href="#" onClick={(e) => {
                                                                e.preventDefault();
                                                                navigate(`/admin/viewCustomers/${customer.user_id}?tab=usage`);
                                                            }}>
                                                                <img src={window.getPublicUrl('admin/images/view-data.svg')} alt="view-data" /> View data usage
                                                            </a>
                                                            <a className="dropdown-item" href="#" onClick={(e) => {
                                                                e.preventDefault();
                                                                navigate(`/admin/viewCustomers/${customer.user_id}?tab=payments`);
                                                            }}>
                                                                <img src={window.getPublicUrl('admin/images/payment.svg')} alt="payment" /> Payment history
                                                            </a>
                                                            <a className="dropdown-item" href="#" onClick={(e) => {
                                                                e.preventDefault();
                                                                openEmailModal(customer);
                                                            }}>
                                                                <img src={window.getPublicUrl('admin/images/send-email.svg')} alt="email" /> Send email
                                                            </a>
                                                           
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="no-data text-center">
                                            {searchQuery ? 'No customers found matching your search.' : 'No customers found.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Pagination */}
                {!loading && customers.length > 0 &&(
                    <div className="pagination-container">
                        {console.log('Rendering pagination with state:', pagination)}
                        {/* <div className="pagination-info">
                            Showing {((parseInt(pagination.current_page, 10) - 1) * pagination.per_page) + 1} to {Math.min(parseInt(pagination.current_page, 10) * pagination.per_page, pagination.total)} of {pagination.total} customers
                        </div> */}
                        <div className="pagination-controls">
                            <button 
                                onClick={handlePreviousPage} 
                                disabled={parseInt(pagination.current_page, 10) === 1}
                                className="pagination-btn pagination-btn-privious"
                            >
                                <img src={window.getPublicUrl('admin/images/back-button.svg')} />
                                 {/* <img src={window.getPublicUrl('admin/images/enable-privious.svg')} />  */}
                            </button>
                            
                            {getPageNumbers().map(pageNum => (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`pagination-btn pagination-btn-no ${pageNum === parseInt(pagination.current_page, 10) ? 'active' : ''}`}
                                >
                                    {pageNum}
                                </button>
                            ))}
                            
                            <button 
                                onClick={handleNextPage} 
                                className="pagination-btn pagination-btn-next"
                                disabled={parseInt(pagination.current_page, 10) === parseInt(pagination.last_page, 10)}
                            >
                                <img src={window.getPublicUrl('admin/images/next-arrow.svg')} /> 
                                   {/* <img src={window.getPublicUrl('admin/images/back-button.svg')} /> */}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <Modal
                isOpen={showEmailModal}
                onRequestClose={emailLoading ? undefined : closeEmailModal}
                style={customStyles}
                contentLabel="Send Email Modal"
            >
                <div className="paymentsuccess send-email-modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Send Email</h5>
                            <button
                                type="button"
                                className="close"
                                onClick={closeEmailModal}
                                aria-label="Close"
                                disabled={emailLoading}
                            >
                                <img src={window.getPublicUrl('admin/images/close-icon.svg')} alt="" />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-sm-12">
                                    <div className="form-group">
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            placeholder="admin@travelsim.com"
                                            name="email"
                                            value={emailForm.email}
                                            onChange={handleEmailFormChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="form-group">
                                        <label>Subject</label>
                                        <div className="select-option w-100">
                                            <select
                                                name="subject"
                                                className="w-100"
                                                value={emailForm.subject}
                                                onChange={handleEmailFormChange}
                                            >
                                                <optgroup label="Special Offers">
                                                    <option value="Special Offer">Special Offer</option>
                                                </optgroup>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea
                                            className="w-100"
                                            name="description"
                                            rows="5"
                                            value={emailForm.description}
                                            onChange={handleEmailFormChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-cancel" onClick={closeEmailModal} disabled={emailLoading}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-save"
                                onClick={handleSendEmail}
                                disabled={emailLoading}
                            >
                                {emailLoading ? 'Sending...' : 'Send Email'}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
}

export default Customers;
